<template>
  <v-card flat>
    <!-- Confirm modal -->
    <ConfirmationModal
      ref="confirmationModal"
      :title="confirmationTitle"
      :subtitle="confirmationSubtitle"
      :message="confirmationMessage"
      confirmColor="error darken-1"
    />
    <v-row align="center" justify="space-between">
      <v-col cols="12" md="9">
        <Header
          :title="`Address Information`"
          subtitle="Manage the address of the customer"
        />
      </v-col>
    </v-row>

    <!-- Living address details -->
    <div v-if="localLivingAddress" class="mt-5">
      <v-card flat outlined class="mb-2">
        <v-card-text>
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="7">
              <div class="d-flex flex-column">
                <span class="text-decoration-underline font-weight-medium"
                  >Home/Living Address</span
                >
                <span class="text-body-2 text--secondary"
                  >{{ localLivingAddress.street }}
                  {{ localLivingAddress.city }}</span
                >
                <span
                  >{{ localLivingAddress.province }},
                  {{ localLivingAddress.country }}</span
                >
              </div>
            </v-col>
            <v-col class="text-md-right">
              <v-btn
                small
                @click="
                  openForm(localLivingAddress);
                  addressType = 'living';
                "
                text
                outlined
                class="mr-3 text-capitalize shadow"
              >
                Edit</v-btn
              >
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>
    <!-- mailing address details -->
    <div v-if="localMailingAddress" class="mt-3">
      <v-card flat outlined class="mb-2">
        <v-card-text>
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="7">
              <div class="d-flex flex-column">
                <span class="font-weight-medium text-decoration-underline"
                  >Mailing Address</span
                >
                <span class="text-body-2 text--secondary"
                  >{{ localMailingAddress.street }}
                  {{ localMailingAddress.city }}</span
                >
                <span
                  >{{ localMailingAddress.province }},
                  {{ localMailingAddress.country }}</span
                >
              </div>
            </v-col>
            <v-col class="text-md-right">
              <v-btn
                small
                @click="
                  openForm(localMailingAddress);
                  addressType = 'mailing';
                "
                text
                outlined
                class="mr-3 text-capitalize shadow"
                >Edit</v-btn
              >
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <div class="mt-5" v-if="!localLivingAddress">
      <v-btn
        @click="
          openForm(localLivingAddress);
          addressType = 'living';
        "
        depressed
        small
        outlined
        color="primary"
        min-height="38"
        class="text-capitalize"
        >Add Living Address</v-btn
      >
    </div>
    <div class="mt-5" v-if="!localMailingAddress">
      <v-btn
        @click="
          openForm(localMailingAddress);
          addressType = 'mailing';
        "
        text
        depressed
        small
        outlined
        color="primary"
        min-height="38"
        class="shadow-md text-capitalize"
      >
        <v-icon left>mdi-plus</v-icon>
        Add Mailing Address</v-btn
      >
    </div>

    <ModalFormLayout
      @submit="onSubmit"
      @cancel="onClose"
      ref="addressModal"
      :options="modalOptions"
    >
      <template #content>
        <ValidationObserver ref="observer">
          <v-row class="mt-2" dense>
            <v-col cols="12" sm="8">
              <ValidationProvider
                name="Street"
                rules="required|min:3"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="Street"
                  :error-messages="errors"
                  v-model="details.street"
                >
                  <template v-slot:label>
                    Street
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="4">
              <ValidationProvider
                name="Date at Address"
                :rules="`required|date|date_less_than:${yesterday}`"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  type="date"
                  label="Date at Address"
                  :error-messages="errors"
                  v-model="details.dateAtAddress"
                >
                  <template v-slot:label>
                    Date at Address
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="City"
                rules="required|min:2"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="City"
                  :error-messages="errors"
                  v-model="details.city"
                >
                  <template v-slot:label>
                    City
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Country"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <CountryPicker
                  dense
                  outlined
                  required
                  label="Country"
                  :error-messages="errors"
                  v-model="details.country"
                  @input="onCountryInput"
                >
                </CountryPicker>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Province"
                rules="required|min:2"
                events="['blur']"
                v-slot="{ errors }"
              >
                <ProvincePicker
                  dense
                  outlined
                  :required="true"
                  label="Province"
                  :error-messages="errors"
                  v-model="details.province"
                ></ProvincePicker>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Zipcode"
                rules="min:5|max:20"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="Zipcode"
                  :error-messages="errors"
                  v-model="details.zipCode"
                >
                </v-text-field>
              </ValidationProvider>
            </v-col>

            <v-col cols="12" sm="6" v-if="addressType == 'living'">
              <ValidationProvider
                name="Nationality"
                events="['blur']"
                v-slot="{ errors }"
              >
                <NationalityPicker
                  dense
                  outlined
                  label="Nationality"
                  :error-messages="errors"
                  v-model="details.nationality"
                ></NationalityPicker>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Document Type"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  hint="For example: Bank Statement, Letter, etc."
                  label="Proof Of Address Document"
                  :error-messages="errors"
                  v-model="details.documentType"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
          </v-row>
        </ValidationObserver>
      </template>
    </ModalFormLayout>
  </v-card>
</template>

<script>
import pageMixin from '@/mixins/page.mixin';
import Header from './Header.vue';
import CountryPicker from '@/modules/shared/components/pickers/CountryPicker.vue';
import ProvincePicker from '@/modules/shared/components/pickers/ProvincePicker.vue';
import NationalityPicker from '@/modules/shared/components/pickers/NationalityPicker.vue';
import { yesterday } from '@/lib/helpers';
import { mapGetters } from 'vuex';

export default {
  components: {
    Header,
    CountryPicker,
    ProvincePicker,
    NationalityPicker
  },
  mixins: [pageMixin],
  props: {
    outlined: {
      type: Boolean,
      default: true
    },

    livingAddress: {
      type: Object,
      default: () => ({})
    },
    mailingAddress: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      details: {},
      localLivingAddress: this.livingAddress,
      localMailingAddress: this.mailingAddress,
      addressType: null,
      yesterday: yesterday(),
      // Confirmation modal options
      confirmationTitle: '',
      confirmationMessage: '',
      confirmationSubtitle: '',

      modalOptions: {
        title: 'Add an address',
        subtitle: 'Enter the values and click submit',
        submitButtonText: 'Submit',
        maxWidth: '600px',
        width: '600px',
        color: 'white',
        flat: true
      }
    };
  },
  emits: ['updateLiving', 'updateMailing'],

  computed: {
    ...mapGetters('coreModule', ['nationalities']),
  },
  methods: {
    onCountryInput(countryName) {
      if (this.addressType === 'living' && !this.details.nationality) {
        const match = this.nationalities.find((n) => n.country === countryName);
        if (match) {
          this.$set(this.details, 'nationality', match.nationality);
        }
      }
    },
    setLivingAddress(info) {
      this.localLivingAddress = info;

      //if(this.person)
    },
    setMailingAddress(info) {
      this.localMailingAddress = info;
    },
    // Open the form for creating/editing a new source
    openForm(item) {
      this.modalOptions.title = 'Create a address';
      this.modalOptions.subtitle = 'A new address will be created.';
      if (item) {
        this.details = item;
        this.modalOptions.title = `Update address`;
        this.modalOptions.subtitle = `Updating address details.`;
      }

      //Normalize country
      const country =
        this.details?.country?.trim() ||
        this.details?.countryOfResidence?.trim();

      if (this.details && country) {
        this.details.country = country;
      }

      //Default country if none exist for the person.
      if (this.details && !country) {
        this.details.country = this.activeCompany.country;
      }

      this.$refs.addressModal.open();
    },
    onClose() {
      this.$refs.observer?.reset();
      this.details = {};
    },
    async onSubmit() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;

      if (this.addressType === 'living') {
        this.localLivingAddress = { ...this.details, type: 'Home' };
        this.$emit('updateLiving', this.localLivingAddress);
      }
      if (this.addressType === 'mailing') {
        this.localMailingAddress = { ...this.details, type: 'Mailing' };
        this.$emit('updateMailing', this.localMailingAddress);
      }
      this.onClose();
      this.$refs.addressModal.close();
    }
  }
};
</script>
